import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PickupService } from '../../services/pickup/pickupService';

/**
 * Vista pública para previsualizar un albarán (recibo) usando react-pdf.
 * - Renderiza el blob PDF recibido desde el endpoint público /show-receipt
 * - Incluye controles de zoom y paginación
 * - Maneja errores y estados de carga
 * - Diseño responsive
 */
const ShowReceipt: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();

  const pageTitle = useMemo(() => 'Previsualización de albarán', []);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const service = new PickupService();

    const openInChromeViewer = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!hash || hash.trim().length === 0) {
          throw new Error('No se ha proporcionado ningún identificador de albarán.');
        }

        await service.showReceipt(hash);
        const response = service.getResponse();

        const contentType = (response?.headers?.['content-type'] as string | undefined)?.toLowerCase() || '';
        const data = response?.data as Blob | null;

        if (!data) {
          throw new Error('No se ha recibido el archivo del albarán.');
        }

        // Si el backend devuelve JSON (error), extraemos el mensaje
        if (contentType.includes('application/json')) {
          try {
            const text = await (data as Blob).text();
            const json = JSON.parse(text);
            const msg = json?.message || json?.errors?.map((e: any) => e?.message).join(', ');
            throw new Error(msg || 'No se ha encontrado ningún albarán disponible o está caducado el enlace.');
          } catch {
            throw new Error('No se ha encontrado ningún albarán disponible o está caducado el enlace.');
          }
        }

        // Aseguramos el tipo para que el visor nativo de Chrome lo reconozca como PDF
        const pdfBlob = data.type && data.type !== ''
          ? data
          : new Blob([data], { type: 'application/pdf' });

        // Usamos el hash de la URL como nombre del archivo para el visor integrado de Chrome.
        // Chrome tomará el nombre del File al descargar desde el visor.
        const safeNameBase = ('albaran' + hash).replace(/[^A-Za-z0-9._-]/g, '_');
        const fileForViewer = new File([pdfBlob], `${safeNameBase}.pdf`, { type: 'application/pdf' });
        const objectUrl = URL.createObjectURL(fileForViewer);

        // Abrimos el visor nativo del navegador en esta misma pestaña
        window.location.replace(objectUrl);
        return;
        
      } catch (e: any) {
        if (mounted) {
          console.error(e);
          setError(e?.message || 'Ha ocurrido un error al cargar el albarán.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    openInChromeViewer();
    return () => { mounted = false; };
  }, [hash]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="w-full bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900" aria-live="polite">
            {pageTitle}
          </h1>
        </div>
      </header>

      <main className="flex-1 flex flex-col min-h-0">
        <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 flex-1 flex flex-col items-center justify-center">
          {loading && (
            <div className="flex flex-col items-center justify-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" aria-hidden="true"></div>
              <p className="mt-4 text-sm text-gray-700">Preparando visor…</p>
              <p className="mt-1 text-xs text-gray-500">Si no se abre automáticamente, asegúrese de que su navegador es compatible.</p>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-md border border-red-300 bg-red-50 p-4 max-w-xl" role="alert">
              <p className="text-sm text-red-800">{error}</p>
              <p className="mt-2 text-xs text-red-700">No se ha podido abrir el visor de PDF integrado.</p>
            </div>
          )}
        </section>
      </main>

      <footer className="mt-auto w-full py-4 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Gema Industrial Residuos
      </footer>
    </div>
  );
};

export default ShowReceipt;